import type { BaseType, Selection } from 'd3-selection'
import type { Dictionary } from '../../../models'
import type { Config as BaseConfig, Params, Size } from '../abstract.component'
// @ts-strict-ignore
import { range } from 'd3-array'
import { scaleBand, scaleLinear } from 'd3-scale'

import { areaRadial, curveLinearClosed } from 'd3-shape'
import { AbstractComponent } from '../abstract.component'

import 'd3-transition'

export class RadarChartComponent extends AbstractComponent<Config, Data> {
  private static COMPONENT_CLASS = 'epb-radar-chart'
  private static FILTER_ID = 'outline'
  private static COMPONENT_TEMPLATE = `<svg class="${RadarChartComponent.COMPONENT_CLASS}" shape-rendering="geometricprecision">
  <defs>
    <filter id="${RadarChartComponent.FILTER_ID}">
      <feMorphology in="SourceAlpha" result="DILATED" operator="dilate" radius="5"></feMorphology>
      <feFlood flood-color="var(--color-neutral-0)" flood-opacity="1" result="WHITE"></feFlood>
      <feComposite in="WHITE" in2="DILATED" operator="in" result="OUTLINE"></feComposite>
      <feMerge>
        <feMergeNode in="OUTLINE"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>
  <g class="${RadarChartComponent.COMPONENT_CLASS}__axis-group"></g>
</svg>`

  private static AXIS_TEXT_WIDTH = 80
  private static Y_AXIS_STEPS = 5

  private size: Size

  private element: Selection<SVGSVGElement, any, BaseType, any>

  private scaleX = scaleBand()
  private scaleY = scaleLinear()

  public constructor(containerEl: HTMLElement, params: Params<Config, Data> = {}) {
    super(containerEl, params)

    containerEl.innerHTML = RadarChartComponent.COMPONENT_TEMPLATE
    this.size = this.getElementSize(containerEl)

    this.element = this.containerElement.select<SVGSVGElement>('svg')

    this.updateScales()

    this.renderAxis()

    this.renderData()
  }

  update(params: Params<Config, Data>): void {
    super.update(params)

    this.updateScales()

    this.renderAxis()

    this.renderData()
  }

  protected getDefaultConfig(): Config {
    return Object.assign(super.getDefaultConfig(), {
      values: [],
      iconSize: 20,
      iconMargin: 5,
    })
  }

  protected get data(): Data {
    const data = (!!super.data && super.data.slice()) || [] // Default value: empty array
    for (let index = data.length; index < 3; index++) {
      const values = {}
      this.config.values.forEach((valueId: string) => {
        values[valueId] = 0
      })
      data.push({
        id: index.toString(),
        values,
      })
    }
    return data
  }

  protected set data(data: Data) {
    super.data = data
  }

  protected resize(containerElSize: Size): void {
    this.size = containerElSize

    this.updateScales()

    this.renderAxis(0)

    this.renderData(0)
  }

  private updateScales(): void {
    const scaleXBandwidth = (2 * Math.PI) / this.data.length / 2
    this.scaleX
      .range([scaleXBandwidth, 2 * Math.PI + scaleXBandwidth])
      .domain(this.data.map((dataItem: DataItem) => dataItem.id))

    this.scaleY
      .range([
        10,
        Math.min(this.size.height, this.size.width) / 2 - RadarChartComponent.AXIS_TEXT_WIDTH,
      ])
      .domain([0, this.getDataMaxValue()])
  }

  private getDataMaxValue(): number {
    const dataMaxValue = this.data.reduce((maxValue: number, dataItem: DataItem) => {
      this.config.values.forEach((valueId: string) => {
        maxValue = Math.max(dataItem.values[valueId], maxValue)
      })
      return maxValue
    }, 0)
    const multiplier = dataMaxValue < 100 ? 10 : 100
    return Math.max(
      Math.ceil(dataMaxValue / multiplier) * multiplier,
      this.config.maxValue || multiplier,
    )
  }

  private renderAxis(transitionsDurationInMillis = this.config.transitionsDurationInMillis): void {
    const axisGroup = this.element
      .select<SVGGElement>(`.${RadarChartComponent.COMPONENT_CLASS}__axis-group`)
      .style('transform', `translate3d(${this.size.width / 2}px, ${this.size.height / 2}px, 0)`)
    this.renderYAxis(axisGroup, transitionsDurationInMillis)
    this.renderXAxis(axisGroup, transitionsDurationInMillis)
  }

  private renderYAxis(
    axisGroup: Selection<SVGGElement, any, BaseType, any>,
    transitionsDurationInMillis: number,
  ): void {
    const series = axisGroup
      .selectAll<SVGGElement, number>(`.${RadarChartComponent.COMPONENT_CLASS}__axis-serie--y`)
      .data(range(0, RadarChartComponent.Y_AXIS_STEPS + 1))
    this.removeAxisSeries(series, transitionsDurationInMillis)
    this.updateYAxisSeries(series, transitionsDurationInMillis)
    this.enterYAxisSeries(series, transitionsDurationInMillis)
  }

  private updateYAxisSeries(
    series: Selection<SVGGElement, number, SVGGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    series.transition().duration(transitionsDurationInMillis).style('opacity', 1)
    series
      .selectAll(`.${RadarChartComponent.COMPONENT_CLASS}__axis-circle`)
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('r', (datum: number) => this.getYAxisCircleRadius(datum))
    series
      .selectAll(`.${RadarChartComponent.COMPONENT_CLASS}__axis-text`)
      .text((datum: number) => this.getYAxisText(datum))
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('y', (datum: number) => this.getYAxisTextPositionY(datum))
  }

  private getYAxisCircleRadius(index: number): number {
    return Math.max(this.scaleY(this.getYAxisValueY(index)), 0)
  }

  private getYAxisValueY(index: number): number {
    return (this.getDataMaxValue() / RadarChartComponent.Y_AXIS_STEPS) * index
  }

  private getYAxisText(index: number): string {
    let value = this.getYAxisValueY(index)
    let valueUnits = this.config.valueUnits || ''
    if (Math.floor(value / 1000000) > 0) {
      value /= 1000000
      valueUnits = `m${valueUnits}`
    } else if (Math.floor(value / 1000) > 0) {
      value /= 1000
      valueUnits = `k${valueUnits}`
    }
    const valueText = value.toLocaleString(this.config.locale, {
      maximumFractionDigits: 1,
    })
    return `${valueText}${valueUnits}`
  }

  private getYAxisTextPositionY(index: number): number {
    return -this.scaleY(this.getYAxisValueY(index))
  }

  private enterYAxisSeries(
    series: Selection<SVGGElement, number, SVGGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    const enterSeries = series
      .enter()
      .append('g')
      .attr(
        'class',
        `${RadarChartComponent.COMPONENT_CLASS}__axis-serie ${RadarChartComponent.COMPONENT_CLASS}__axis-serie--y`,
      )
    enterSeries
      .style('opacity', 0)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('opacity', 1)
    enterSeries
      .append('circle')
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-circle`)
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('r', (datum: number) => this.getYAxisCircleRadius(datum))
    enterSeries
      .append('text')
      .filter((datum: number) => datum > 0)
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-text`)
      .attr('filter', `url(#${RadarChartComponent.FILTER_ID})`)
      .text((datum: number) => this.getYAxisText(datum))
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('y', (datum: number) => this.getYAxisTextPositionY(datum))
  }

  private renderXAxis(
    axisGroup: Selection<SVGGElement, any, BaseType, any>,
    transitionsDurationInMillis: number,
  ): void {
    const series = axisGroup
      .selectAll<SVGGElement, DataItem>(`.${RadarChartComponent.COMPONENT_CLASS}__axis-serie--x`)
      .data(this.data, (dataItem: DataItem) => dataItem.id)
    this.removeAxisSeries(series, transitionsDurationInMillis)
    this.updateXAxisSeries(series, transitionsDurationInMillis)
    this.enterXAxisSeries(series, transitionsDurationInMillis)
  }

  private removeAxisSeries(
    series: Selection<SVGGElement, number | DataItem, SVGGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    const exitSeries = series.exit()
    exitSeries.transition().duration(transitionsDurationInMillis).style('opacity', 0)
    exitSeries.transition().delay(transitionsDurationInMillis).remove()
  }

  private updateXAxisSeries(
    series: Selection<SVGGElement, DataItem, SVGGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    series.transition().duration(transitionsDurationInMillis).style('opacity', 1)
    series
      .select(`.${RadarChartComponent.COMPONENT_CLASS}__axis-line`)
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('x2', (dataItem: DataItem) => this.getXAxisLinePositionX(dataItem))
      .attr('y2', (dataItem: DataItem) => this.getXAxisLinePositionY(dataItem))
    series
      .select(`.${RadarChartComponent.COMPONENT_CLASS}__axis-label-wrapper`)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('transform', (dataItem: DataItem) => {
        const { x, y } = this.getXAxisTextIconPosition(dataItem)
        return `translate3d(${x}px, ${y}px, 0)`
      })
    series
      .select(`.${RadarChartComponent.COMPONENT_CLASS}__axis-icon`)
      .text((dataItem: DataItem) => dataItem.iconContent)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('width', `${this.config.iconSize}px`)
      .style('height', `${this.config.iconSize}px`)
      .style('margin-right', `${this.config.iconMargin}px`)
    series
      .select(`.${RadarChartComponent.COMPONENT_CLASS}__axis-text`)
      .text((dataItem: DataItem) => dataItem.label)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('width', `${RadarChartComponent.AXIS_TEXT_WIDTH}px`)
  }

  private getXAxisLinePositionX(dataItem: DataItem): number {
    const angle = this.scaleX(dataItem.id)
    const radius = this.scaleY.range()[1]
    return Math.sin(angle) * radius
  }

  private getXAxisLinePositionY(dataItem: DataItem): number {
    const angle = this.scaleX(dataItem.id)
    const radius = this.scaleY.range()[1]
    return -(Math.cos(angle) * radius)
  }

  private getXAxisTextIconPosition(dataItem: DataItem): { x: number; y: number } {
    return {
      x: this.getXAxisTextIconPositionX(dataItem),
      y: this.getXAxisTextIconPositionY(dataItem),
    }
  }

  private getXAxisTextIconPositionX(dataItem: DataItem): number {
    const angle = this.scaleX(dataItem.id)
    const sin = Math.sin(angle)
    if (Number(sin.toFixed(2)) === 0) {
      return (
        -(this.config.iconSize + this.config.iconMargin + RadarChartComponent.AXIS_TEXT_WIDTH) / 2
      )
    }
    const radius = this.scaleY.range()[1] + RadarChartComponent.AXIS_TEXT_WIDTH / 2
    let x = sin * radius
    if (angle > Math.PI) {
      x -= this.config.iconMargin + RadarChartComponent.AXIS_TEXT_WIDTH
    }
    return x
  }

  private getXAxisTextIconPositionY(dataItem: DataItem): number {
    const angle = this.scaleX(dataItem.id)
    const radius = this.scaleY.range()[1] + RadarChartComponent.AXIS_TEXT_WIDTH / 2
    return -(Math.cos(angle) * radius)
  }

  private enterXAxisSeries(
    series: Selection<SVGGElement, DataItem, SVGGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    const enterSeries = series
      .enter()
      .append('g')
      .attr(
        'class',
        `${RadarChartComponent.COMPONENT_CLASS}__axis-serie ${RadarChartComponent.COMPONENT_CLASS}__axis-serie--x`,
      )
    enterSeries
      .style('opacity', 0)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('opacity', 1)
    enterSeries
      .append('line')
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-line`)
      .attr('x2', (dataItem: DataItem) => this.getXAxisLinePositionX(dataItem))
      .attr('y2', (dataItem: DataItem) => this.getXAxisLinePositionY(dataItem))
    const textIcon = enterSeries
      .append('foreignObject')
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-label-wrapper`)
      .style('transform', (dataItem: DataItem) => {
        const { x, y } = this.getXAxisTextIconPosition(dataItem)
        return `translate3d(${x}px, ${y}px, 0)`
      })
      .append('xhtml:div')
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-label`)
    textIcon
      .append('xhtml:span')
      .filter((dataItem: DataItem) => !!dataItem.iconContent)
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-icon eicon`)
      .text((dataItem: DataItem) => dataItem.iconContent)
      .style('width', `${this.config.iconSize}px`)
      .style('height', `${this.config.iconSize}px`)
      .style('margin-right', `${this.config.iconMargin}px`)
    textIcon
      .append('h5')
      .filter((dataItem: DataItem) => !!dataItem.label)
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__axis-text`)
      .text((dataItem: DataItem) => dataItem.label)
      .style('width', `${RadarChartComponent.AXIS_TEXT_WIDTH}px`)
  }

  private renderData(transitionsDurationInMillis = this.config.transitionsDurationInMillis): void {
    const series = this.element
      .selectAll<SVGGElement, ValueItem>(`.${RadarChartComponent.COMPONENT_CLASS}__value-serie`)
      .data(
        this.getDataByValue(),
        (valueItem: ValueItem) => `${valueItem.id}-${valueItem.data.length}`,
      )
    this.removeValueSeries(series, transitionsDurationInMillis)
    this.updateValueSeries(series, transitionsDurationInMillis)
    this.enterValueSeries(series, transitionsDurationInMillis)
    series.order()
  }

  private getDataByValue(): ValueItem[] {
    const dataByValue = []
    this.config.values.forEach((valueId: string) => {
      dataByValue.push({
        id: valueId,
        data: this.data.map((dataItem: DataItem) => ({
          id: dataItem.id,
          label: dataItem.label,
          value: dataItem.values[valueId],
        })),
      })
    })
    return dataByValue
  }

  private removeValueSeries(
    series: Selection<SVGGElement, ValueItem, SVGSVGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    const exitSeries = series.exit()
    exitSeries.transition().duration(transitionsDurationInMillis).style('opacity', 0)
    exitSeries.transition().delay(transitionsDurationInMillis).remove()
  }

  private updateValueSeries(
    series: Selection<SVGGElement, ValueItem, SVGSVGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    series
      .style('transform', `translate3d(${this.size.width / 2}px, ${this.size.height / 2}px, 0)`)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('opacity', 1)
    series
      .select(`.${RadarChartComponent.COMPONENT_CLASS}__value-area`)
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('d', (valueItem: ValueItem) => this.getValueAreaCommands(valueItem))
  }

  private getValueAreaCommands(valueItem: ValueItem): string {
    return areaRadial<ValueDataItem>()
      .curve(curveLinearClosed)
      .angle((valueDataItem: ValueDataItem) => this.scaleX(valueDataItem.id))
      .outerRadius((valueDataItem: ValueDataItem) => this.scaleY(valueDataItem.value))(
      valueItem.data,
    )
  }

  private enterValueSeries(
    series: Selection<SVGGElement, ValueItem, SVGSVGElement, any>,
    transitionsDurationInMillis: number,
  ): void {
    const enterSeries = series
      .enter()
      .append('g')
      .attr('class', (valueItem: ValueItem) => this.getValueSerieClass(valueItem))
      .style('transform', `translate3d(${this.size.width / 2}px, ${this.size.height / 2}px, 0)`)
    enterSeries
      .style('opacity', 0)
      .transition()
      .duration(transitionsDurationInMillis)
      .style('opacity', 1)
    enterSeries
      .append('path')
      .attr('class', `${RadarChartComponent.COMPONENT_CLASS}__value-area`)
      .attr('d', (valueItem: ValueItem) => this.getValuePathInitialCommands(valueItem))
      .transition()
      .duration(transitionsDurationInMillis)
      .attr('d', (valueItem: ValueItem) => this.getValueAreaCommands(valueItem))
  }

  private getValueSerieClass(valueItem: ValueItem): string {
    return `${RadarChartComponent.COMPONENT_CLASS}__value-serie ${RadarChartComponent.COMPONENT_CLASS}__value-serie--${valueItem.id}`
  }

  private getValuePathInitialCommands(valueItem: ValueItem): string {
    return areaRadial<ValueDataItem>()
      .curve(curveLinearClosed)
      .angle((valueDataItem: ValueDataItem) => this.scaleX(valueDataItem.id))
      .outerRadius(0)(valueItem.data)
  }
}

export interface Config extends BaseConfig {
  values: string[]
  maxValue?: number
  valueUnits?: string
  iconSize: number
  iconMargin: number
}

export type Data = DataItem[]

export interface DataItem {
  id: string
  label?: string
  values: Dictionary<number>
  iconContent?: string
}

interface ValueItem {
  id: string
  data: ValueDataItem[]
}

interface ValueDataItem {
  id: string
  label?: string
  value: number
}
